import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";

const FIELDS = [
  ["name", "Name", ["name", "fullname", "clientname", "customername", "leadname"]],
  ["phone", "Phone", ["phone", "mobile", "mobileno", "phonenumber", "contact", "contactno", "number"]],
  ["alt_phone", "Alternate phone", ["altphone", "alternatephone", "alternate", "phone2", "mobile2"]],
  ["email", "Email", ["email", "emailid", "mail"]],
  ["lead_type", "Lead type", ["leadtype", "type", "clienttype"]],
  ["property_type", "Property type", ["propertytype", "property"]],
  ["locations", "Locations / sectors", ["location", "locations", "sector", "sectors", "area", "city", "preferredlocation"]],
  ["budget_min", "Budget min", ["budgetmin", "minbudget", "budgetfrom"]],
  ["budget_max", "Budget max", ["budgetmax", "maxbudget", "budgetto", "budget"]],
  ["area_min", "Area min", ["areamin", "minarea", "size"]],
  ["area_max", "Area max", ["areamax", "maxarea"]],
  ["bhk", "BHK", ["bhk", "configuration", "rooms"]],
  ["purpose", "Purpose", ["purpose"]],
  ["source", "Source", ["source", "leadsource", "portal"]],
  ["temperature", "Temperature", ["temperature", "temp", "priority", "status"]],
  ["stage", "Stage", ["stage"]],
  ["notes", "Notes", ["notes", "remarks", "comment", "comments", "requirement"]],
];
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

function guess(headers) {
  const used = new Set();
  const map = {};
  FIELDS.forEach(([k, , syn]) => {
    const i = headers.findIndex((h, idx) => !used.has(idx) && syn.includes(norm(h)));
    if (i >= 0) {
      map[k] = String(i);
      used.add(i);
    }
  });
  return map;
}

export default function LeadImportDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [skip, setSkip] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setPreview(null);
    setMapping({});
    setResult(null);
  };

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import/preview", fd);
      setPreview(data);
      setMapping(guess(data.headers));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const m = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/leads/import", { mapping: m, rows: preview.rows, skip_duplicates: skip });
      setResult(data);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="lead-import-dialog">
        <DialogHeader>
          <DialogTitle>Import leads</DialogTitle>
          <DialogDescription>Upload a CSV or Excel file, match its columns, then import.</DialogDescription>
        </DialogHeader>
        {!preview && !result && (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-10 text-center hover:border-primary/50" data-testid="lead-import-dropzone">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <FileSpreadsheet className="h-8 w-8 text-primary" />}
            <span className="text-sm font-semibold">Choose .csv or .xlsx file</span>
            <span className="text-xs text-muted-foreground">First row must contain column headers. Max 5,000 rows.</span>
            <input type="file" accept=".csv,.xlsx,.xlsm" className="hidden" data-testid="lead-import-file-input" onChange={(e) => pick(e.target.files?.[0])} />
          </label>
        )}
        {preview && !result && (
          <div className="space-y-4">
            <p className="text-sm"><span className="font-semibold">{preview.total}</span> rows found. Match each field to a column in your file:</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {FIELDS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2">
                  <span className="w-32 shrink-0 text-xs font-semibold">{label}{["name", "phone"].includes(k) && <span className="text-destructive"> *</span>}</span>
                  <select data-testid={`import-map-${k}`} value={mapping[k] ?? ""} onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })} className="h-9 flex-1 rounded-md border bg-card px-2 text-sm">
                    <option value="">— Skip —</option>
                    {preview.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-secondary"><tr>{preview.headers.map((h, i) => <th key={i} className="whitespace-nowrap px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
                <tbody>{preview.rows.slice(0, 3).map((r, i) => <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="whitespace-nowrap px-2 py-1.5">{c}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={skip} onCheckedChange={(v) => setSkip(!!v)} data-testid="import-skip-duplicates" />
              Skip rows whose phone number already exists
            </label>
          </div>
        )}
        {result && (
          <div className="space-y-2 rounded-xl bg-secondary/60 p-4 text-sm" data-testid="lead-import-result">
            <p><span className="font-semibold">{result.imported}</span> leads imported.</p>
            <p>{result.skipped_duplicates} duplicates skipped · {result.error_count} rows with errors.</p>
            {result.errors.slice(0, 8).map((e) => <p key={e.row} className="text-xs text-destructive">Row {e.row}: {e.error}</p>)}
          </div>
        )}
        <DialogFooter>
          {preview && !result && (
            <Button onClick={run} disabled={busy || mapping.name === undefined || mapping.name === "" || mapping.phone === undefined || mapping.phone === ""} data-testid="lead-import-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Import {preview.total} rows
            </Button>
          )}
          {result && <Button onClick={() => { onOpenChange(false); reset(); }} data-testid="lead-import-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
